import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  LinearProgress,
  InputAdornment,
  Snackbar,
  Alert,
  CircularProgress,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Divider,
  ClickAwayListener,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  LocationOn,
  SwapVert,
  MyLocation,
  DirectionsCar,
  AccessTime,
  CheckCircle,
  Close,
  Place,
  Cancel,
  ArrowBack,
} from '@mui/icons-material';
import { useSnackbar } from '../contexts/SnackbarContext';
import { vehicleTypesAPI, rideAPI } from '../services/api';
import { useRideStore } from '../store/rideStore';
import { wsService } from '../services/websocket';
import { RIDER_EVENTS } from '../constants/websocket-events';
import { useCurrentLocation } from '../hooks/useCurrentLocation';

interface Location {
  address: string;
  latitude: number;
  longitude: number;
  placeId?: string;
}

interface VehicleType {
  id: string | number;
  name: string;
  description?: string;
  capacity?: number;
  priceMultiplier?: number;
  icon?: string;
  type_name?: string;
  [key: string]: any;
}

interface FareEstimate {
  distance: number;
  duration: number;
  fare: number;
  estimated_fare?: number;
  currency_symbol?: string;
}

interface PlacePrediction {
  place_id: string;
  description: string;
  structured_formatting?: {
    main_text: string;
    secondary_text: string;
  };
}

declare global {
  interface Window {
    google: {
      maps: {
        Map: new (element: HTMLElement, options?: any) => any;
        LatLng: new (lat: number, lng: number) => any;
        LatLngBounds: new () => any;
        Geocoder: new () => any;
        DirectionsService: new () => any;
        DirectionsRenderer: new (options?: any) => any;
        Marker: new (options?: any) => any;
        TravelMode: {
          DRIVING: string;
        };
        ControlPosition: {
          RIGHT_CENTER: any;
        };
        SymbolPath: {
          CIRCLE: any;
        };
        DirectionsStatus: {
          OK: string;
        };
        event: {
          trigger: (instance: any, eventName: string) => void;
        };
        places: {
          AutocompleteService: new () => any;
          PlacesService: new (element: HTMLElement) => any;
        };
      };
    };
  }
}

const OrderPage: React.FC = () => {
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const { createRide, estimateFare } = useRideStore();


  const {
    location: currentLocationData,
    isLoading: isLocationLoading,
    refreshLocation: refreshCurrentLocation,
  } = useCurrentLocation({
    watch: true,
    enableGeocoding: true,
    enableBackendUpdates: true,
    backendUpdateInterval: 10000, 
    minDistance: 10, 
    enableHighAccuracy: true,
  });

  const [origin, setOrigin] = useState<Location | null>(null);
  const [destination, setDestination] = useState<Location | null>(null);
  const [originInput, setOriginInput] = useState('');
  const [destinationInput, setDestinationInput] = useState('');
  const [vehicleTypes, setVehicleTypes] = useState<VehicleType[]>([]);
  const [selectedVehicleType, setSelectedVehicleType] = useState<string | number>('');
  const [fareEstimate, setFareEstimate] = useState<FareEstimate | null>(null);
  const [allEstimates, setAllEstimates] = useState<Record<string, FareEstimate>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isGettingEstimate, setIsGettingEstimate] = useState(false);
  const [isLoadingVehicleTypes, setIsLoadingVehicleTypes] = useState(true);
  const [map, setMap] = useState<any>(null);
  const [directionsService, setDirectionsService] = useState<any>(null);
  const [directionsRenderer, setDirectionsRenderer] = useState<any>(null);
  const [originMarker, setOriginMarker] = useState<any>(null);
  const [destinationMarker, setDestinationMarker] = useState<any>(null);
  const [currentLocationMarker, setCurrentLocationMarker] = useState<any>(null);
  const [locationConfirmed, setLocationConfirmed] = useState(false);
  const [, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<PlacePrediction[]>([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [isSearchingOrigin, setIsSearchingOrigin] = useState(false);
  const [googleMapsDuration, setGoogleMapsDuration] = useState<number | null>(null);
  const [isGoogleMapsLoaded, setIsGoogleMapsLoaded] = useState(false);
  const [createdRideId, setCreatedRideId] = useState<string | null>(null);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [isSearchingDriver, setIsSearchingDriver] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const bookingAbortControllerRef = useRef<AbortController | null>(null);
  const createdRideIdRef = useRef<string | null>(null);

  const mapRef = useRef<HTMLDivElement>(null);
  const originInputRef = useRef<HTMLInputElement>(null);
  const destinationInputRef = useRef<HTMLInputElement>(null);
  const autocompleteServiceRef = useRef<any>(null);
  const placesServiceRef = useRef<any>(null);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || import.meta.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'AIzaSyBPX_T5tQ6lbqFvCNYXi9v-orNYgcU0628';


  useEffect(() => {
    const fetchVehicleTypes = async () => {
      try {
        setIsLoadingVehicleTypes(true);
        const response = await vehicleTypesAPI.getVehicleTypes();

        let types: VehicleType[] = [];
        if (Array.isArray(response)) {
          types = response;
        } else if (response.data && Array.isArray(response.data)) {
          types = response.data;
        } else if (response.success && Array.isArray(response.data)) {
          types = response.data;
        }

        if (types.length > 0) {
          setVehicleTypes(types);
          setSelectedVehicleType(types[0].id);
        } else {
          showSnackbar('Nəqliyyat növləri tapılmadı', 'warning');
        }
      } catch (error: any) {
        console.error('Failed to fetch vehicle types:', error);
        showSnackbar('Nəqliyyat növləri yüklənə bilmədi', 'error');
      } finally {
        setIsLoadingVehicleTypes(false);
      }
    };

    fetchVehicleTypes();
  }, []);


  useEffect(() => {

    if (window.google && window.google.maps && window.google.maps.places) {
      console.log('✅ Google Maps already loaded');
      setIsGoogleMapsLoaded(true);
      return;
    }


    const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
    if (existingScript) {
      console.log('⏳ Google Maps script already exists, waiting for load...');
      const checkInterval = setInterval(() => {
        if (window.google && window.google.maps && window.google.maps.places) {
          clearInterval(checkInterval);
          console.log('✅ Google Maps loaded from existing script');
          setIsGoogleMapsLoaded(true);
        }
      }, 100);

      setTimeout(() => {
        clearInterval(checkInterval);
        if (!window.google || !window.google.maps) {
          console.error('❌ Google Maps failed to load from existing script');
        }
      }, 10000);
      return;
    }


    console.log('📦 Loading Google Maps script...');
    const script = document.createElement('script');
    script.id = 'google-maps-script';
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places,geometry&loading=async`;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      console.log('✅ Google Maps script loaded');
      setTimeout(() => {
        if (window.google && window.google.maps && window.google.maps.places) {
          setIsGoogleMapsLoaded(true);
        } else {
          console.error('❌ Google Maps not available after script load');
          showSnackbar('Google Maps yüklənə bilmədi', 'error');
        }
      }, 300);
    };

    script.onerror = () => {
      console.error('❌ Failed to load Google Maps script');
      showSnackbar('Google Maps yüklənə bilmədi', 'error');
    };

    document.head.appendChild(script);


    return () => {

    };
  }, []);


  const initializePlacesServices = useCallback(() => {
    if (!window.google || !window.google.maps || !window.google.maps.places) {
      setTimeout(initializePlacesServices, 200);
      return;
    }

    try {
      autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
      placesServiceRef.current = new window.google.maps.places.PlacesService(
        document.createElement('div')
      );
      console.log('✅ Places services initialized');
    } catch (error) {
      console.error('Error initializing Places services:', error);
    }
  }, []);

  const initializeMap = useCallback(() => {
    if (!mapRef.current || !window.google || !window.google.maps) {
      console.log('⚠️ Map ref or Google Maps not ready');
      return;
    }

    if (map) {
      console.log('⚠️ Map already initialized');
      return;
    }

    console.log('🗺️ Initializing map...');
    try {

      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const { latitude, longitude } = position.coords;
            if (!mapRef.current) return;
            const mapInstance = new window.google.maps.Map(mapRef.current, {
              center: { lat: latitude, lng: longitude },
              zoom: 15,
              mapTypeControl: false,
              fullscreenControl: false,
              streetViewControl: false,
              zoomControl: true,
              zoomControlOptions: {
                position: window.google.maps.ControlPosition.RIGHT_CENTER,
              },
            });
            initializeMapWithInstance(mapInstance);
          },
          (error) => {
            console.warn('⚠️ Geolocation failed, using default location:', error);

            if (!mapRef.current) return;
            const mapInstance = new window.google.maps.Map(mapRef.current, {
              center: { lat: 40.4093, lng: 49.8671 }, 
              zoom: 13,
              mapTypeControl: false,
              fullscreenControl: false,
              streetViewControl: false,
              zoomControl: true,
              zoomControlOptions: {
                position: window.google.maps.ControlPosition.RIGHT_CENTER,
              },
            });
            initializeMapWithInstance(mapInstance);
          },
          {
            enableHighAccuracy: true,
            timeout: 30000, 
            maximumAge: 0, 
          }
        );
      } else {

        if (!mapRef.current) return;
        const mapInstance = new window.google.maps.Map(mapRef.current, {
          center: { lat: 40.4093, lng: 49.8671 }, 
          zoom: 13,
          mapTypeControl: false,
          fullscreenControl: false,
          streetViewControl: false,
          zoomControl: true,
          zoomControlOptions: {
            position: window.google.maps.ControlPosition.RIGHT_CENTER,
          },
        });
        initializeMapWithInstance(mapInstance);
      }
    } catch (error) {
      console.error('❌ Error initializing map:', error);
      showSnackbar('Xəritə başlatıla bilmədi', 'error');
    }
  }, [map]);

  const initializeMapWithInstance = useCallback((mapInstance: any) => {
    if (!mapInstance) return;

    try {
      setMap(mapInstance);

      const directionsServiceInstance = new window.google.maps.DirectionsService();
      const directionsRendererInstance = new window.google.maps.DirectionsRenderer({
        suppressMarkers: false,
        preserveViewport: false,
        polylineOptions: {
          strokeColor: '#FF006E',
          strokeWeight: 5,
          strokeOpacity: 0.8,
        },
      });


      directionsRendererInstance.setMap(mapInstance);

      setDirectionsService(directionsServiceInstance);
      setDirectionsRenderer(directionsRendererInstance);

      console.log('✅ Map and directions services initialized');


      setTimeout(() => {
        if (mapInstance && window.google && window.google.maps && window.google.maps.event) {
          try {
            window.google.maps.event.trigger(mapInstance, 'resize');
            const center = mapInstance.getCenter();
            if (center) {
              mapInstance.setCenter(center);
            }
            console.log('✅ Map resize triggered');
          } catch (e) {
            console.warn('⚠️ Could not trigger map resize:', e);
          }
        }
      }, 500);


      setTimeout(() => {
        if (mapInstance && window.google && window.google.maps && window.google.maps.event) {
          try {
            window.google.maps.event.trigger(mapInstance, 'resize');
            const center = mapInstance.getCenter();
            if (center) {
              mapInstance.setCenter(center);
            }
          } catch (e) {
            console.warn('⚠️ Second resize attempt failed:', e);
          }
        }
      }, 1500);
    } catch (error) {
      console.error('❌ Error initializing map:', error);
      showSnackbar('Xəritə başlatıla bilmədi', 'error');
    }
  }, [showSnackbar]);


  useEffect(() => {
    if (currentLocationData && !origin) {
      const address = currentLocationData.address || `${currentLocationData.latitude.toFixed(6)}, ${currentLocationData.longitude.toFixed(6)}`;
      setOriginInput(address);
      setOrigin({
        address,
        latitude: currentLocationData.latitude,
        longitude: currentLocationData.longitude,
      });


      if (map && window.google && window.google.maps) {
        map.setCenter({ lat: currentLocationData.latitude, lng: currentLocationData.longitude });
        map.setZoom(15);


        if (currentLocationMarker) {
          currentLocationMarker.setPosition({ lat: currentLocationData.latitude, lng: currentLocationData.longitude });
        } else {
          const marker = new window.google.maps.Marker({
            position: { lat: currentLocationData.latitude, lng: currentLocationData.longitude },
            map: map,
            title: 'Mənim mövqeyim',
            icon: {
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 10,
              fillColor: '#4CAF50',
              fillOpacity: 1,
              strokeColor: '#FFFFFF',
              strokeWeight: 3,
            },
            zIndex: 1000,
          });
          setCurrentLocationMarker(marker);
        }
      }

      if (currentLocationData.address) {
        setLocationConfirmed(true);
        setTimeout(() => setLocationConfirmed(false), 3000);
      }
    }
  }, [currentLocationData, origin, map, currentLocationMarker]);


  useEffect(() => {
    if (currentLocationData && currentLocationMarker && map) {
      currentLocationMarker.setPosition({ 
        lat: currentLocationData.latitude, 
        lng: currentLocationData.longitude 
      });
    }
  }, [currentLocationData?.latitude, currentLocationData?.longitude, currentLocationMarker, map]);


  const getCurrentLocation = useCallback(async () => {
    if (!currentLocationData) {

      await refreshCurrentLocation();
      return;
    }


    const { latitude, longitude, address } = currentLocationData;
    const finalAddress = address || `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;

    setOriginInput(finalAddress);
    setOrigin({
      address: finalAddress,
      latitude,
      longitude,
    });


    if (map && window.google && window.google.maps) {
      map.setCenter({ lat: latitude, lng: longitude });
      map.setZoom(15);

      if (currentLocationMarker) {
        currentLocationMarker.setPosition({ lat: latitude, lng: longitude });
      }
    }

    if (address) {
      setLocationConfirmed(true);
      setTimeout(() => setLocationConfirmed(false), 3000);
      showSnackbar('Mövqe yeniləndi', 'success');
    } else {

      refreshCurrentLocation();
    }
  }, [currentLocationData, map, currentLocationMarker, refreshCurrentLocation, showSnackbar]);


  useEffect(() => {
    if (isGoogleMapsLoaded && !map && window.google && window.google.maps) {
      initializeMap();
      initializePlacesServices();
    }
  }, [isGoogleMapsLoaded, map, initializeMap, initializePlacesServices]);


  useEffect(() => {
    if (map && isGoogleMapsLoaded && currentLocationData && !origin) {

      console.log('🗺️ Map ready, location available');
    }
  }, [map, isGoogleMapsLoaded, currentLocationData, origin]);

  const reverseGeocode = async (lat: number, lng: number, retries: number = 3): Promise<string> => {
    return new Promise((resolve, reject) => {
      if (!window.google || !window.google.maps) {

        if (retries > 0) {
          setTimeout(() => {
            reverseGeocode(lat, lng, retries - 1).then(resolve).catch(reject);
          }, 500);
          return;
        }
        reject(new Error('Google Maps not loaded'));
        return;
      }

      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode(
        { 
          location: { lat, lng },
          language: 'az', 
        },
        (results: any[], status: string) => {
          if (status === 'OK' && results && results.length > 0) {

            let address = results[0].formatted_address;


            for (const result of results) {
              if (result.types.includes('street_address') || result.types.includes('route')) {
                address = result.formatted_address;
                break;
              }
            }

            resolve(address);
          } else if (retries > 0) {

            setTimeout(() => {
              reverseGeocode(lat, lng, retries - 1).then(resolve).catch(reject);
            }, 500);
          } else {
            reject(new Error(`Geocoding failed: ${status}`));
          }
        }
      );
    });
  };


  const searchPlaces = useCallback((query: string, isOrigin: boolean = false) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current as any);
    }

    if (!query || query.length < 2) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    setIsSearchingOrigin(isOrigin);
    setSearchQuery(query);

    searchTimeoutRef.current = setTimeout(() => {
      if (!autocompleteServiceRef.current) {
        console.log('AutocompleteService not ready, retrying...');
        setTimeout(() => searchPlaces(query, isOrigin), 200);
        return;
      }

      try {
        const centerLat = origin?.latitude || 40.4093;
        const centerLng = origin?.longitude || 49.8671;
        const location = new window.google.maps.LatLng(centerLat, centerLng);

        autocompleteServiceRef.current.getPlacePredictions(
          {
            input: query,
            locationBias: { center: location, radius: 50000 },
            componentRestrictions: { country: 'az' },
          },
          (predictions: PlacePrediction[] | null, status: string): void => {
            if (status === 'OK' && predictions) {
              setSearchResults(predictions.slice(0, 5));
              setShowSearchResults(true);
            } else {
              setSearchResults([]);
              if (status !== 'ZERO_RESULTS') {
                console.warn('Places search status:', status);
              }
            }
          }
        );
      } catch (error) {
        console.error('Places search error:', error);
        setSearchResults([]);
      }
    }, 300);
  }, [origin]);


  const selectPlace = useCallback((placeId: string, isOrigin: boolean = false) => {
    console.log('🔍 selectPlace called:', { placeId, isOrigin });


    setShowSearchResults(false);
    setSearchQuery('');

    if (!placesServiceRef.current) {
      console.log('PlacesService not ready, retrying...');
      setTimeout(() => selectPlace(placeId, isOrigin), 200);
      return;
    }

    try {
      placesServiceRef.current.getDetails(
        {
          placeId,
          fields: ['geometry', 'formatted_address', 'name', 'place_id', 'address_components'],
        },
        (place: any, status: string): void => {
          console.log('📍 Place details received:', { status, place });

          if (status === 'OK' && place && place.geometry && place.geometry.location) {
            const location = {
              address: place.formatted_address || place.name || '',
              latitude: place.geometry.location.lat(),
              longitude: place.geometry.location.lng(),
              placeId: place.place_id,
            };

            console.log('✅ Setting location:', location);

            if (isOrigin) {
              setOrigin(location);
              setOriginInput(location.address);
            } else {
              setDestination(location);
              setDestinationInput(location.address);
            }

            if (map) {
              map.setCenter({ lat: location.latitude, lng: location.longitude });
              map.setZoom(15);
            }




            showSnackbar('Məkan seçildi', 'success');
          } else {
            console.error('Place details error:', status);
            showSnackbar('Məkan seçilə bilmədi', 'error');
          }
        }
      );
    } catch (error) {
      console.error('Place details error:', error);
      showSnackbar('Məkan seçilə bilmədi', 'error');
    }
  }, [map, origin, directionsService, directionsRenderer, showSnackbar]);


  useEffect(() => {
    if (origin && destination && directionsService && directionsRenderer && map && window.google) {
      const timer = setTimeout(() => {
        calculateRoute();
        if (vehicleTypes.length > 0) {
          calculateAllEstimates(vehicleTypes);
        }
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setFareEstimate(null);
      if (directionsRenderer) {
        try {
          directionsRenderer.setDirections({ routes: [] });
        } catch (e) {
          console.error('Error clearing directions:', e);
        }
      }
    }
  }, [origin?.latitude, origin?.longitude, destination?.latitude, destination?.longitude, directionsService, directionsRenderer, map, vehicleTypes.length]);

  const calculateRoute = useCallback(() => {
    if (!origin || !destination || !directionsService || !directionsRenderer || !map || !window.google) {
      console.log('Route calculation skipped - missing dependencies');
      return;
    }

    console.log('🛣️ Calculating route from', origin.address, 'to', destination.address);

    if (directionsRenderer.getMap() !== map) {
      directionsRenderer.setMap(map);
    }


    if (window.google && window.google.maps) {

      if (originMarker) {
        originMarker.setMap(null);
      }
      if (destinationMarker) {
        destinationMarker.setMap(null);
      }


      const originMarkerInstance = new window.google.maps.Marker({
        position: { lat: origin.latitude, lng: origin.longitude },
        map: map,
        title: 'Haradan',
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 8,
          fillColor: '#4CAF50',
          fillOpacity: 1,
          strokeColor: '#FFFFFF',
          strokeWeight: 2,
        },
        zIndex: 1001,
      });
      setOriginMarker(originMarkerInstance);


      const destMarkerInstance = new window.google.maps.Marker({
        position: { lat: destination.latitude, lng: destination.longitude },
        map: map,
        title: 'Hara',
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 8,
          fillColor: '#F44336',
          fillOpacity: 1,
          strokeColor: '#FFFFFF',
          strokeWeight: 2,
        },
        zIndex: 1001,
      });
      setDestinationMarker(destMarkerInstance);
    }

    try {
      directionsService.route(
        {
          origin: { lat: origin.latitude, lng: origin.longitude },
          destination: { lat: destination.latitude, lng: destination.longitude },
          travelMode: window.google.maps.TravelMode.DRIVING,
        },
        (result: any, status: string): void => {
          console.log('🛣️ Route calculation status:', status);

          if (status === 'OK' && result && result.routes && result.routes.length > 0) {
            console.log('✅ Route calculated successfully');

            try {

              directionsRenderer.setOptions({
                suppressMarkers: true, 
                polylineOptions: {
                  strokeColor: '#FF006E',
                  strokeWeight: 5,
                  strokeOpacity: 0.8,
                },
              });

              directionsRenderer.setDirections(result);


              const route = result.routes[0];
              if (route.legs && route.legs.length > 0 && route.legs[0].duration) {
                const durationInSeconds = route.legs[0].duration.value;
                setGoogleMapsDuration(durationInSeconds);
              }


              const bounds = new window.google.maps.LatLngBounds();
              bounds.extend({ lat: origin.latitude, lng: origin.longitude });
              bounds.extend({ lat: destination.latitude, lng: destination.longitude });

              if (route.legs && route.legs.length > 0) {
                route.legs.forEach((leg: any) => {
                  if (leg.start_location) bounds.extend(leg.start_location);
                  if (leg.end_location) bounds.extend(leg.end_location);
                });
              }

              if (!bounds.isEmpty()) {
                setTimeout(() => {
                  map.fitBounds(bounds, {
                    top: 150,
                    right: 50,
                    bottom: 350,
                    left: 50,
                    padding: 50,
                  });
                }, 300);
              }

              console.log('✅ Route rendered successfully on map');
            } catch (renderError: any) {
              console.error('❌ Error rendering route:', renderError);
              showSnackbar('Marşrut göstərilə bilmədi', 'error');
            }
          } else {
            console.warn('⚠️ Route calculation failed:', status);
            showSnackbar('Marşrut tapıla bilmədi', 'warning');
          }
        }
      );
    } catch (error: any) {
      console.error('❌ Error in calculateRoute:', error);
      showSnackbar('Marşrut hesablanarkən xəta baş verdi', 'error');
    }
  }, [origin, destination, directionsService, directionsRenderer, map, originMarker, destinationMarker]);

  const calculateAllEstimates = async (types: VehicleType[]) => {
    if (!origin || !destination || types.length === 0) return;

    setIsGettingEstimate(true);
    const estimates: Record<string, FareEstimate> = {};

    const estimatePromises = types.map(async (vehicleType) => {
      const vehicleTypeId = String(vehicleType.id);
      if (!vehicleTypeId) return;

      try {
        const estimate = await estimateFare({
          start_point: {
            latitude: origin.latitude,
            longitude: origin.longitude,
            address: origin.address,
          },
          end_point: {
            latitude: destination.latitude,
            longitude: destination.longitude,
            address: destination.address,
          },
          vehicle_type_id: vehicleTypeId,
        });

        let estimateData: any = estimate;
        if (estimate.data) {
          estimateData = estimate.data;
        } else if (Array.isArray(estimate) && estimate.length > 0) {
          estimateData = estimate[0];
        }

        if (estimateData) {
          const distance = estimateData.distance || estimateData.distanceKm || 0;
          const duration = estimateData.duration || estimateData.durationMinutes || (googleMapsDuration || 0);
          const fare = estimateData.fare || estimateData.estimated_fare || estimateData.price || 0;

          estimates[vehicleTypeId] = {
            distance,
            duration,
            fare,
            estimated_fare: fare,
            currency_symbol: estimateData.currency_symbol || '₼',
          };
        }
      } catch (error) {
        console.error(`Estimate error for vehicle type ${vehicleTypeId}:`, error);

        if (googleMapsDuration) {
          const distance = calculateDistance(origin.latitude, origin.longitude, destination.latitude, destination.longitude);
          const baseFare = 2 + (distance * 0.5);
          const priceMultiplier = vehicleType.priceMultiplier || 1;

          estimates[vehicleTypeId] = {
            distance,
            duration: googleMapsDuration,
            fare: baseFare * priceMultiplier,
            estimated_fare: baseFare * priceMultiplier,
            currency_symbol: '₼',
          };
        }
      }
    });

    await Promise.all(estimatePromises);
    setAllEstimates(estimates);

    if (selectedVehicleType && estimates[selectedVehicleType]) {
      setFareEstimate(estimates[selectedVehicleType]);
    } else if (types.length > 0 && estimates[String(types[0].id)]) {
      setFareEstimate(estimates[String(types[0].id)]);
    }

    setIsGettingEstimate(false);
  };

  const calculateDistance = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
    const R = 6371; 
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLng / 2) * Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  const handleGetCurrentLocation = useCallback((e?: React.MouseEvent | React.TouchEvent) => {

    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    console.log('📍 Getting current location manually...');


    setTimeout(() => {
      getCurrentLocation();
    }, 100);
  }, [getCurrentLocation]);

  const handleSwapLocations = () => {
    if (!origin || !destination) return;

    const tempOrigin = origin;
    const tempOriginInput = originInput;

    setOrigin(destination);
    setDestination(tempOrigin);
    setOriginInput(destinationInput);
    setDestinationInput(tempOriginInput);
  };

  const handleCancelRideClick = () => {
    setIsCancelDialogOpen(true);
  };

  const handleCancelRide = async () => {
    setIsCancelDialogOpen(false);
    setIsCancelling(true);


    if (bookingAbortControllerRef.current) {
      console.log('🔄 Aborting booking process...');
      bookingAbortControllerRef.current.abort();
    }


    if (isLoading && !createdRideId) {
      console.log('🔄 Cancelling ride booking process (before ride creation)...');
      setIsLoading(false);
      setIsSearchingDriver(false);
      setIsCancelling(false);
      showSnackbar('Sifariş ləğv edildi', 'success');
      return;
    }


    if (createdRideId) {
      try {
        console.log('🔄 Cancelling ride via API:', createdRideId);
        const response = await rideAPI.cancelRide(createdRideId, 'İstifadəçi tərəfindən ləğv edildi');
        console.log('✅ Cancel ride response:', response);
        showSnackbar('Sifariş ləğv edildi', 'success');
        setCreatedRideId(null);
        createdRideIdRef.current = null;
        setIsSearchingDriver(false);
        setIsLoading(false);

        setOrigin(null);
        setDestination(null);
        setOriginInput('');
        setDestinationInput('');
        setIsCancelling(false);
        return;
      } catch (error: any) {
        console.error('❌ Cancel ride error:', error);
        const errorMessage = error?.response?.data?.message || error?.response?.data?.error || error?.message || 'Sifariş ləğv edilə bilmədi';
        showSnackbar(errorMessage, 'error');
        setIsCancelling(false);
        return;
      }
    }


    if (isLoading) {
      console.log('🔄 Waiting for ride creation to complete, then will cancel...');


      let attempts = 0;
      const maxAttempts = 50; 

      const checkAndCancel = async () => {
        while (attempts < maxAttempts && !createdRideIdRef.current) {
          await new Promise(resolve => setTimeout(resolve, 100));
          attempts++;
        }


        const rideIdToCancel = createdRideIdRef.current;
        if (rideIdToCancel) {
          try {
            console.log('🔄 Ride was created, cancelling now:', rideIdToCancel);
            const response = await rideAPI.cancelRide(rideIdToCancel, 'İstifadəçi tərəfindən ləğv edildi');
            console.log('✅ Cancel ride response:', response);
            showSnackbar('Sifariş ləğv edildi', 'success');
            setCreatedRideId(null);
            createdRideIdRef.current = null;
            setIsSearchingDriver(false);
            setIsLoading(false);

            setOrigin(null);
            setDestination(null);
            setOriginInput('');
            setDestinationInput('');
            setIsCancelling(false);
          } catch (error: any) {
            console.error('❌ Cancel ride error:', error);
            const errorMessage = error?.response?.data?.message || error?.response?.data?.error || error?.message || 'Sifariş ləğv edilə bilmədi';
            showSnackbar(errorMessage, 'error');
            setIsCancelling(false);
          }
        } else {

          console.log('🔄 Ride booking cancelled successfully');
          setIsLoading(false);
          setIsSearchingDriver(false);
          setIsCancelling(false);
          showSnackbar('Sifariş ləğv edildi', 'success');
        }
      };

      checkAndCancel();
    } else {
      setIsCancelling(false);
      showSnackbar('Sifariş tapılmadı', 'warning');
    }
  };

  const handleBookRide = async () => {
    if (!origin || !destination) {
      showSnackbar('Zəhmət olmasa, hər iki ünvanı daxil edin', 'error');
      return;
    }

    if (!selectedVehicleType) {
      showSnackbar('Zəhmət olmasa, nəqliyyat növü seçin', 'error');
      return;
    }


    bookingAbortControllerRef.current = new AbortController();
    const abortSignal = bookingAbortControllerRef.current.signal;

    try {
      setIsLoading(true);
      setIsSearchingDriver(true);


      console.log('🔵 Connecting WebSocket...');
      try {
        await wsService.ensureConnection('rider');
        await new Promise(resolve => setTimeout(resolve, 300));
        console.log('🔵 WebSocket connected:', wsService.isConnected());
      } catch (wsError: any) {
        if (abortSignal.aborted) {
          console.log('WebSocket connection cancelled');
          return;
        }
        console.error('WebSocket connection error:', wsError);
      }

      if (abortSignal.aborted) {
        console.log('Ride booking cancelled before creating ride');
        return;
      }

      const rideData = {
        start_point: {
          latitude: origin.latitude,
          longitude: origin.longitude,
          address: origin.address,
        },
        end_point: {
          latitude: destination.latitude,
          longitude: destination.longitude,
          address: destination.address,
        },
        vehicle_type_id: selectedVehicleType,
        estimated_fare: fareEstimate?.fare || fareEstimate?.estimated_fare || 0,
      };

      const ride = await createRide(rideData);


      if (abortSignal.aborted) {
        console.log('🔄 Booking was cancelled, cancelling ride...');

        if (ride && ride.id) {
          try {
            const rideIdString = ride.id?.toString() || String(ride.id);
            console.log('🔄 Cancelling ride immediately after creation:', rideIdString);
            await rideAPI.cancelRide(rideIdString, 'İstifadəçi tərəfindən ləğv edildi');
            console.log('✅ Ride cancelled successfully');
            setIsLoading(false);
            setIsSearchingDriver(false);
            return;
          } catch (cancelError) {
            console.error('❌ Error cancelling ride after creation:', cancelError);

            setIsLoading(false);
            setIsSearchingDriver(false);
            return;
          }
        }
        setIsLoading(false);
        setIsSearchingDriver(false);
        return;
      }


      if (abortSignal.aborted) {
        if (ride && ride.id) {
          try {
            const rideIdString = ride.id?.toString() || String(ride.id);
            await rideAPI.cancelRide(rideIdString, 'İstifadəçi tərəfindən ləğv edildi');
          } catch (cancelError) {
            console.error('Error cancelling ride:', cancelError);
          }
        }
        setIsLoading(false);
        setIsSearchingDriver(false);
        return;
      }

      if (ride) {

        if (abortSignal.aborted) {
          console.log('🔄 Cancelling ride before setting ID...');
          try {
            const rideIdString = ride.id?.toString() || String(ride.id);
            await rideAPI.cancelRide(rideIdString, 'İstifadəçi tərəfindən ləğv edildi');
          } catch (cancelError) {
            console.error('Error cancelling ride:', cancelError);
          }
          setIsLoading(false);
          setIsSearchingDriver(false);
          return;
        }

        const rideIdString = ride.id?.toString() || String(ride.id);
        setCreatedRideId(rideIdString);
        createdRideIdRef.current = rideIdString;
        setIsLoading(false);
        showSnackbar('Sifarişiniz qəbul edildi! Sürücü axtarılır...', 'success');


        const handleDriverRejected = (data: any) => {
          console.log('🔵 Driver rejected:', data);
          const dataRideId = data.ride_id || data.rideId || data.id;
          if (dataRideId === rideIdString || dataRideId === ride.id) {
            showSnackbar('Sürücü sifarişi qəbul etmədi. Başqa sürücü axtarılır...', 'warning');
          }
        };

        wsService.on(RIDER_EVENTS.RIDE_DRIVER_REJECTED, handleDriverRejected);
        wsService.on('ride:driver_rejected', handleDriverRejected);
        wsService.on('driver:rejected', handleDriverRejected);





      }
    } catch (error: any) {
      if (abortSignal.aborted) {
        console.log('Ride booking was cancelled');
        return;
      }
      console.error('Failed to book ride:', error);
      showSnackbar(error.message || 'Sifariş zamanı xəta baş verdi', 'error');
      setIsSearchingDriver(false);
    } finally {
      if (!abortSignal.aborted) {
        setIsLoading(false);
      }
      bookingAbortControllerRef.current = null;
    }
  };

  const getVehicleIcon = (vehicleType: VehicleType) => {
    if (vehicleType.icon) return vehicleType.icon;
    if (vehicleType.capacity && vehicleType.capacity >= 6) return '🚐';
    if (vehicleType.capacity && vehicleType.capacity >= 4) return '🚙';
    return '🚗';
  };

  const getVehicleName = (vehicleType: VehicleType) => {
    return vehicleType.type_name?.trim() || 
           vehicleType.name?.trim() || 
           vehicleType.vehicle_type?.name?.trim() || 
           'Standard';
  };

  return (
    <Box sx={{ 
      minHeight: '100vh',
      height: 'auto',
      display: 'flex', 
      flexDirection: 'column', 
      overflow: 'visible',
      position: 'relative',
      WebkitOverflowScrolling: 'touch',
      overscrollBehavior: 'contain',
    }}>
      {}
      <IconButton
        onClick={() => navigate('/app')}
        sx={{
          position: 'fixed',
          top: 70,
          left: 16,
          zIndex: 1200,
          bgcolor: 'white',
          boxShadow: 2,
          minWidth: 48,
          minHeight: 48,
          '&:hover': {
            bgcolor: 'grey.100',
          },
          touchAction: 'manipulation',
          WebkitTapHighlightColor: 'transparent',
        }}
      >
        <ArrowBack />
      </IconButton>
      {}
      <Paper
        elevation={8}
        sx={{
          bgcolor: '#FF006E',
          color: 'white',
          p: 2,
          zIndex: 1000,
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          borderRadius: '0 0 16px 16px',
        }}
      >
        {}
        <ClickAwayListener onClickAway={() => setShowSearchResults(false)}>
          <Box sx={{ position: 'relative', mb: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: 'white', borderRadius: 2, px: { xs: 0.5, sm: 1 } }}>
              <LocationOn sx={{ color: '#FF006E', mr: { xs: 0.5, sm: 1 }, fontSize: { xs: 20, sm: 24 } }} />
              <TextField
                inputRef={originInputRef}
                fullWidth
                placeholder="Haradan?"
                value={originInput}
                onChange={(e) => {
                  const value = e.target.value;
                  setOriginInput(value);
                  searchPlaces(value, true);
                }}
                onFocus={() => {
                  if (originInput.length >= 2) {
                    setShowSearchResults(true);
                  }
                }}
                variant="standard"
                InputProps={{
                  disableUnderline: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton 
                        size="medium" 
                        onClick={handleGetCurrentLocation}
                        onTouchStart={handleGetCurrentLocation}
                        disabled={isLocationLoading}
                        sx={{ 
                          color: '#FF006E',
                          minWidth: { xs: 48, sm: 48 },
                          minHeight: { xs: 48, sm: 48 },
                          width: { xs: 48, sm: 48 },
                          height: { xs: 48, sm: 48 },
                          touchAction: 'manipulation',
                          WebkitTapHighlightColor: 'transparent',
                          WebkitTouchCallout: 'none',
                          userSelect: 'none',
                          cursor: 'pointer',
                          '&:active': {
                            transform: 'scale(0.95)',
                          },
                        }}
                        aria-label="Cari mövqeyi əlavə et"
                      >
                        {isLocationLoading ? (
                          <CircularProgress size={24} />
                        ) : (
                          <MyLocation sx={{ fontSize: { xs: 24, sm: 24 } }} />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiInputBase-input': {
                    py: { xs: 1.25, sm: 1.5 },
                    fontSize: { xs: '0.9rem', sm: '0.95rem' },
                  },
                }}
              />
            </Box>

            {}
            {showSearchResults && isSearchingOrigin && searchResults.length > 0 && (
              <Paper
                elevation={4}
                sx={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  mt: 0.5,
                  maxHeight: 300,
                  overflow: 'auto',
                  zIndex: 1100,
                }}
              >
                <List dense>
                  {searchResults.map((result, index) => (
                    <React.Fragment key={result.place_id || index}>
                      <ListItemButton
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          console.log('📍 Selecting place:', result.place_id);
                          selectPlace(result.place_id, true);
                        }}
                        onTouchStart={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          console.log('📍 Selecting place (touch):', result.place_id);
                          selectPlace(result.place_id, true);
                        }}
                        sx={{ 
                          py: 1.5,
                          minHeight: 56,
                          touchAction: 'manipulation',
                          WebkitTapHighlightColor: 'transparent',
                          WebkitTouchCallout: 'none',
                          userSelect: 'none',
                          '&:active': {
                            backgroundColor: 'action.selected',
                            transform: 'scale(0.98)',
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          <Place color="action" />
                        </ListItemIcon>
                        <ListItemText
                          primary={result.structured_formatting?.main_text || result.description}
                          secondary={result.structured_formatting?.secondary_text}
                          primaryTypographyProps={{ noWrap: true }}
                          secondaryTypographyProps={{ noWrap: true }}
                        />
                      </ListItemButton>
                      {index < searchResults.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              </Paper>
            )}
          </Box>
        </ClickAwayListener>

        {}
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 0.5 }}>
          <IconButton 
            onClick={handleSwapLocations}
            sx={{ 
              bgcolor: 'rgba(255,255,255,0.2)',
              color: 'white',
              minWidth: 48,
              minHeight: 48,
              touchAction: 'manipulation',
              WebkitTapHighlightColor: 'transparent',
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.3)',
              },
              '&:active': {
                bgcolor: 'rgba(255,255,255,0.4)',
              },
            }}
            size="medium"
          >
            <SwapVert />
          </IconButton>
        </Box>

        {}
        <ClickAwayListener onClickAway={() => setShowSearchResults(false)}>
          <Box sx={{ position: 'relative' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: 'white', borderRadius: 2, px: { xs: 0.5, sm: 1 } }}>
              <LocationOn sx={{ color: '#2196F3', mr: { xs: 0.5, sm: 1 }, fontSize: { xs: 20, sm: 24 } }} />
              <TextField
                inputRef={destinationInputRef}
                fullWidth
                placeholder="Hara?"
                value={destinationInput}
                onChange={(e) => {
                  const value = e.target.value;
                  setDestinationInput(value);
                  searchPlaces(value, false);
                }}
                onFocus={() => {
                  if (destinationInput.length >= 2) {
                    setShowSearchResults(true);
                  }
                }}
                variant="standard"
                InputProps={{
                  disableUnderline: true,
                }}
                sx={{
                  '& .MuiInputBase-input': {
                    py: { xs: 1.25, sm: 1.5 },
                    fontSize: { xs: '0.9rem', sm: '0.95rem' },
                  },
                }}
              />
            </Box>

            {}
            {showSearchResults && !isSearchingOrigin && searchResults.length > 0 && (
              <Paper
                elevation={4}
                sx={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  mt: 0.5,
                  maxHeight: 300,
                  overflow: 'auto',
                  zIndex: 1100,
                }}
              >
                <List dense>
                  {searchResults.map((result, index) => (
                    <React.Fragment key={result.place_id || index}>
                      <ListItemButton
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          console.log('📍 Selecting place:', result.place_id);
                          selectPlace(result.place_id, false);
                        }}
                        onTouchStart={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          console.log('📍 Selecting place (touch):', result.place_id);
                          selectPlace(result.place_id, false);
                        }}
                        sx={{ 
                          py: 1.5,
                          minHeight: 56,
                          touchAction: 'manipulation',
                          WebkitTapHighlightColor: 'transparent',
                          WebkitTouchCallout: 'none',
                          userSelect: 'none',
                          '&:active': {
                            backgroundColor: 'action.selected',
                            transform: 'scale(0.98)',
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          <Place color="action" />
                        </ListItemIcon>
                        <ListItemText
                          primary={result.structured_formatting?.main_text || result.description}
                          secondary={result.structured_formatting?.secondary_text}
                          primaryTypographyProps={{ noWrap: true }}
                          secondaryTypographyProps={{ noWrap: true }}
                        />
                      </ListItemButton>
                      {index < searchResults.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              </Paper>
            )}
          </Box>
        </ClickAwayListener>
      </Paper>

      {}
      <Box sx={{ 
        flex: 1, 
        position: 'relative',
        minHeight: { xs: '400px', sm: '500px' },
        height: { xs: '400px', sm: '500px' },
        width: '100%',
        mb: 2,
      }}>
        <div
          ref={mapRef}
          style={{ 
            width: '100%', 
            height: '100%',
            minHeight: '100%',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
          }}
        />

        {}
        {isLocationLoading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: 'rgba(0,0,0,0.3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
            }}
          >
            <Box sx={{ bgcolor: 'white', p: 3, borderRadius: 2 }}>
              <Typography>Mövqe müəyyən edilir...</Typography>
              <LinearProgress sx={{ mt: 2 }} />
            </Box>
          </Box>
        )}
      </Box>

      {}
      <Snackbar
        open={locationConfirmed}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          severity="success" 
          icon={<CheckCircle />}
          action={
            <IconButton size="small" onClick={() => setLocationConfirmed(false)}>
              <Close fontSize="small" />
            </IconButton>
          }
        >
          Mövqe müəyyən edildi
        </Alert>
      </Snackbar>

      {}
      {origin && destination && (
        <Paper
          elevation={8}
          sx={{
            p: { xs: 1.5, sm: 2 },
            position: 'relative',
            mt: 2,
            mb: 4,
            mx: { xs: 2, sm: 0 },
            borderRadius: 2,
            overflow: 'visible',
            display: 'flex',
            flexDirection: 'column',
            touchAction: 'pan-y',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            Nəqliyyat növü
          </Typography>

          {isLoadingVehicleTypes ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Grid container spacing={{ xs: 1, sm: 2 }} sx={{ mb: 2 }}>
                {vehicleTypes.map((type) => {
                  const typeId = String(type.id);
                  const isSelected = selectedVehicleType === typeId;
                  const typeEstimate = allEstimates[typeId];
                  const duration = typeEstimate?.duration || googleMapsDuration || 0;
                  const fare = typeEstimate?.fare || typeEstimate?.estimated_fare || 0;

                  return (
                    <Grid item xs={4} sm={4} key={typeId}>
                      <Card
                        sx={{
                          cursor: 'pointer',
                          border: isSelected ? '2px solid #FF006E' : '1px solid #e0e0e0',
                          borderRadius: 2,
                          transition: 'all 0.2s',
                          '&:hover': {
                            boxShadow: 4,
                            transform: 'translateY(-2px)',
                          },
                        }}
                        onClick={() => {
                          setSelectedVehicleType(typeId);
                          if (typeEstimate) {
                            setFareEstimate(typeEstimate);
                          }
                        }}
                      >
                        <CardContent sx={{ textAlign: 'center', p: 1.5, '&:last-child': { pb: 1.5 } }}>
                          <Typography variant="h4" gutterBottom>
                            {getVehicleIcon(type)}
                          </Typography>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {getVehicleName(type)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {type.description || `${type.capacity || 4} nəfərə qədər`}
                          </Typography>
                          {duration > 0 && (
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                              {Math.round(duration / 60)} dəq
                            </Typography>
                          )}
                          {fare > 0 && (
                            <Typography variant="body2" color="primary" sx={{ fontWeight: 700, mt: 0.5 }}>
                              ₼{fare.toFixed(2)}
                            </Typography>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>

              {}
              {isGettingEstimate ? (
                <LinearProgress sx={{ mb: 2 }} />
              ) : fareEstimate ? (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, p: 1.5, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                  <Box display="flex" alignItems="center">
                    <AccessTime color="action" fontSize="small" sx={{ mr: 1 }} />
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {Math.round((fareEstimate.duration || googleMapsDuration || 0) / 60)} dəqiqə
                    </Typography>
                  </Box>
                  <Typography variant="h6" color="primary" sx={{ fontWeight: 700 }}>
                    {fareEstimate.currency_symbol || '₼'}{fareEstimate.fare.toFixed(2)}
                  </Typography>
                </Box>
              ) : null}

              {}
              {createdRideId && !isLoading && (
                <Box sx={{ mt: 2 }}>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={handleCancelRideClick}
                    startIcon={<Cancel />}
                    fullWidth
                    sx={{ 
                      py: 1.5,
                      borderRadius: 2,
                      fontSize: { xs: '0.9rem', sm: '1rem' },
                      fontWeight: 600,
                      touchAction: 'manipulation',
                      WebkitTapHighlightColor: 'transparent',
                      '&:active': {
                        transform: 'scale(0.98)',
                        transition: 'transform 0.1s ease-out',
                      },
                    }}
                  >
                    Axtarışı Ləğv Et
                  </Button>
                </Box>
              )}

              <Box sx={{ pt: 2, mt: 2 }}>
                {createdRideId ? (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={() => navigate(`/app/track/${createdRideId}`)}
                      startIcon={<DirectionsCar />}
                      size="large"
                      sx={{ 
                        py: 2.5,
                        bgcolor: '#FF006E',
                        borderRadius: 2,
                        fontSize: { xs: '1rem', sm: '1.1rem' },
                        fontWeight: 600,
                        textTransform: 'none',
                        minHeight: { xs: 56, sm: 60 },
                        boxShadow: 4,
                        touchAction: 'manipulation',
                        WebkitTapHighlightColor: 'transparent',
                        flex: 1,
                      }}
                    >
                      Sifarişi İzlə
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={handleCancelRideClick}
                      startIcon={<Cancel />}
                      size="large"
                      disabled={isLoading}
                      sx={{ 
                        py: 2.5,
                        borderRadius: 2,
                        fontSize: { xs: '1rem', sm: '1.1rem' },
                        fontWeight: 600,
                        textTransform: 'none',
                        minHeight: { xs: 56, sm: 60 },
                        minWidth: { xs: 120, sm: 140 },
                        touchAction: 'manipulation',
                        WebkitTapHighlightColor: 'transparent',
                        '&:active': {
                          transform: 'scale(0.98)',
                        },
                      }}
                    >
                      Ləğv et
                    </Button>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={handleBookRide}
                      disabled={!origin || !destination || isLoading || !selectedVehicleType}
                      startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <DirectionsCar />}
                      size="large"
                      sx={{ 
                        py: 2.5,
                        bgcolor: '#FF006E',
                        borderRadius: 2,
                        fontSize: { xs: '1rem', sm: '1.1rem' },
                        fontWeight: 600,
                        textTransform: 'none',
                        minHeight: { xs: 56, sm: 60 },
                        boxShadow: 4,
                        touchAction: 'manipulation',
                        WebkitTapHighlightColor: 'transparent',
                        WebkitTouchCallout: 'none',
                        userSelect: 'none',
                        flex: 1,
                        '&:hover': {
                          bgcolor: '#e0005e',
                          boxShadow: 6,
                        },
                        '&:active': {
                          transform: 'scale(0.98)',
                          bgcolor: '#d0005a',
                        },
                        '&:disabled': {
                          bgcolor: '#ccc',
                          color: '#666',
                          transform: 'none',
                        },
                      }}
                    >
                      {isLoading ? 'Sifariş edilir...' : 'Taksi Sifariş Et'}
                    </Button>
                    {isLoading && (
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={handleCancelRideClick}
                        startIcon={<Cancel />}
                        size="large"
                        sx={{ 
                          py: 2.5,
                          borderRadius: 2,
                          fontSize: { xs: '1rem', sm: '1.1rem' },
                          fontWeight: 600,
                          textTransform: 'none',
                          minHeight: { xs: 56, sm: 60 },
                          minWidth: { xs: 120, sm: 140 },
                          touchAction: 'manipulation',
                          WebkitTapHighlightColor: 'transparent',
                          '&:active': {
                            transform: 'scale(0.98)',
                          },
                        }}
                      >
                        Ləğv et
                      </Button>
                    )}
                  </Box>
                )}
              </Box>
            </>
          )}
        </Paper>
      )}

      {}
      <Dialog
        open={isCancelDialogOpen}
        onClose={() => {
          if (!isCancelling) {
            setIsCancelDialogOpen(false);
          }
        }}
        PaperProps={{
          sx: {
            borderRadius: { xs: 3, sm: 4 },
            minWidth: { xs: '280px', sm: '400px' },
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600, pb: 1 }}>
          {isSearchingDriver && !createdRideId ? 'Axtarışı ləğv et' : 'Sifarişi ləğv et'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
            {isSearchingDriver && !createdRideId 
              ? 'Sürücü axtarışını ləğv etmək istədiyinizə əminsiniz?'
              : 'Sifarişi ləğv etmək istədiyinizə əminsiniz?'}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 2, sm: 3 }, pt: 1 }}>
          <Button
            onClick={() => {
              if (!isCancelling) {
                setIsCancelDialogOpen(false);
              }
            }}
            variant="outlined"
            disabled={isCancelling}
            sx={{
              minWidth: { xs: 100, sm: 120 },
              minHeight: { xs: 44, sm: 48 },
              touchAction: 'manipulation',
            }}
          >
            Xeyr
          </Button>
          <Button
            onClick={handleCancelRide}
            variant="contained"
            color="error"
            disabled={isCancelling}
            sx={{
              minWidth: { xs: 100, sm: 120 },
              minHeight: { xs: 44, sm: 48 },
              touchAction: 'manipulation',
            }}
          >
            {isCancelling ? 'Gözləyin...' : 'Bəli'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrderPage;
